// The workbook in `selectedProjectApp.public` — the shared tier that needs no
// network request at all.
//
// This is the PREFERRED shared tier, and the reason is the one failure the
// attachment cannot get past. An attachment's bytes live in GCS, and reading
// them means fetching a signed `storage.googleapis.com` url FROM THIS ORIGIN.
// The bucket sends `Access-Control-Allow-Origin` for the host
// (`staging.giraffe.build`) and not for the app (`gtables-app.giraffe.build`),
// so the browser blocks a response that arrived intact:
//
//   Access to fetch at 'https://storage.googleapis.com/feaso-prod/…' from
//   origin 'https://gtables-app.giraffe.build' has been blocked by CORS
//   policy: No 'Access-Control-Allow-Origin' header is present.
//   GET … net::ERR_FAILED 200 (OK)
//
// A 200 the browser will not hand over. Nothing in this app can fix that — it
// is a bucket CORS configuration — and no amount of retrying helps, which is
// why the save button sat on "Retry" while writes were succeeding.
//
// `public` has none of that shape: the payload arrives IN the broadcast state
// with the iframe handshake, so reading it is a property access. No fetch, no
// signed url, no CORS, no cold-start `fetchProjectDetails` flake.
//
// What it has instead is a size limit — undocumented, around 20,000 chars, and
// it REJECTS rather than truncates. So the attachment stays as the overflow
// tier for a workbook too big to fit here. Dropping the system sheets' cells
// (see model/persistFormat.ts) is what made that the rare case rather than the
// normal one: a 500-feature project is now ~3.6 kB.
//
// Lifted from ~/Documents/code/canonicalGiraffe/src/sdk/projectAppStore.ts,
// where the call shape was verified against a running Giraffe.

import { readState, updateProjectApp } from './adapter';
import { resolveAppId } from '@/model/persistFormat';

type AnyApp = Record<string, unknown>;

/**
 * Our own key inside `public`, so this app cannot stomp anything else stored
 * there. An older build wrote the workbook to `public` itself, which is why the
 * read below accepts both.
 */
export const APP_DATA_KEY = 'gtables';

export type AppDataWrite =
  | { ok: true }
  | { ok: false; reason: 'no-app' | 'no-id' | 'too-large' | 'rpc-failed'; detail?: string };

function currentApp(): { app: AnyApp | null; appId: string | null } {
  const app = (readState('selectedProjectApp') ?? null) as AnyApp | null;
  if (!app) return { app: null, appId: null };
  return { app, appId: resolveAppId(app, readState('projectAppsByAppID')) };
}

/**
 * The saved workbook from broadcast state, or null.
 *
 * Synchronous, and that is the whole point: there is nothing to await, nothing
 * to retry and nothing that can be refused by a CORS check.
 */
export function readAppData(): unknown | null {
  const { app } = currentApp();
  if (!app) return null;
  const pub = app['public'];
  if (pub === null || pub === undefined || typeof pub !== 'object') return null;
  const own = (pub as AnyApp)[APP_DATA_KEY];
  if (own !== null && own !== undefined) return own;
  // The legacy shape: the workbook written straight to `public`. Recognised by
  // the envelope's own fields, so an unrelated blob is not mistaken for one.
  const legacy = pub as { version?: unknown; workbook?: unknown };
  if (legacy.version !== undefined && legacy.workbook !== undefined) return pub;
  return null;
}

/**
 * Merge the workbook into `public` and persist it.
 *
 * The SDK requires a CLONE — it silently rejects the mutated live object — and
 * the bare app id, because `selectedProjectApp.id` is the composite
 * `<projectId>-<appId>` and `updateProjectApp` answers "app id mismatch" to it.
 */
export async function writeAppData(payload: unknown): Promise<AppDataWrite> {
  const { app, appId } = currentApp();
  if (!app) return { ok: false, reason: 'no-app' };
  if (!appId) return { ok: false, reason: 'no-id' };

  const cloned = structuredClone(app);
  const prev = (cloned['public'] ?? {}) as AnyApp;
  cloned['public'] = { ...prev, [APP_DATA_KEY]: payload };

  const res = await updateProjectApp(appId, cloned);
  if (res.ok) return { ok: true };
  const detail = res.reason ?? '';
  /*
   * Surfaced, never swallowed. A too-large save that reports success is how the
   * caller ends up believing work landed when it did not.
   *
   * The separators matter: builds have answered "too large", "too-large" and
   * "payload too large", and a pattern of `too\s*large` silently missed the
   * hyphenated one — which then read as a generic failure and skipped the
   * fallback to the attachment entirely.
   */
  if (/too[\s_-]*large|payload|exceed|size limit/i.test(detail)) {
    return { ok: false, reason: 'too-large', detail };
  }
  return { ok: false, reason: 'rpc-failed', detail };
}
